import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import DateField from '../components/DateField';
import { competitionsService } from '../../services/competitions';
import { AUTO_REFRESH_INTERVAL, ROUND_REFRESH_CHOICES } from '../../hooks/useAutoRefresh';
import LocationHeader from '../../components/location/LocationHeader';
import { GymInfoFields, type GymInfoValues } from '../../components/gym/GymInfoFields';
import RankingStrategySelect from '../components/RankingStrategySelect';
import StatusCard from '../components/StatusCard';
import LocationDocumentsCard from '../components/LocationDocumentsCard';
import { useGymAdmin } from '../context/gym-admin-context';
import type { Competition, CompetitionStatus, LifecycleStatus } from '../../types/competition';
import type { GalleryImage } from '../../types/location-images';

const EMPTY_INFO: GymInfoValues = {
  websiteUrl: '',
  address: '',
  city: '',
  zipcode: '',
  latitude: '',
  longitude: '',
};

/** The date fields hold `yyyy-MM-dd`; the API speaks ISO timestamps. */
const toDateInput = (value?: string | null) => (value ? value.slice(0, 10) : '');

/**
 * `datetime-local` wants wall-clock `yyyy-MM-ddTHH:mm` with no zone, so the
 * instant is read in the reader's own — which is the desk's, the only clock
 * anybody at the event is looking at.
 */
const toDateTimeInput = (value?: string | null) => {
  if (!value) return '';
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
};

/**
 * A stage's own identity: its name, its weekend, and the header its page is
 * read under.
 *
 * The venue is deliberately not editable here. It is set when the stage is
 * created, from the circuit's panel, because moving a stage to another gym also
 * moves its address — which is a thing to redo rather than to patch a field of.
 */
export default function CompetitionInfoPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { selectedGymSlug: slug } = useGymAdmin();

  const [stage, setStage] = useState<Competition | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [entriesCloseAt, setEntriesCloseAt] = useState('');
  /** Seconds between the round pages' refreshes; 0 is off. */
  const [refreshSeconds, setRefreshSeconds] = useState(AUTO_REFRESH_INTERVAL / 1000);
  /** The stage's own regulation; empty inherits the trophy's. */
  const [rankingStrategy, setRankingStrategy] = useState('');
  /** Whether it takes an entry fee: what puts "paid" on the entry list. */
  const [acceptsPayments, setAcceptsPayments] = useState(false);
  const [savingPayments, setSavingPayments] = useState(false);
  const [info, setInfo] = useState<GymInfoValues>(EMPTY_INFO);
  const [saving, setSaving] = useState(false);
  const [movingStatus, setMovingStatus] = useState(false);

  const load = useCallback(async (stageSlug: string) => {
    setLoading(true);
    try {
      setStage(await competitionsService.getCompetitionBySlug(stageSlug));
    } catch {
      setStage(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (slug) load(slug);
  }, [slug, load]);

  useEffect(() => {
    if (!stage) return;
    setName(stage.name);
    setStartDate(toDateInput(stage.start_date));
    setEndDate(toDateInput(stage.end_date));
    setEntriesCloseAt(toDateTimeInput(stage.entries_close_at));
    setRefreshSeconds(stage.round_refresh_seconds ?? AUTO_REFRESH_INTERVAL / 1000);
    setRankingStrategy(stage.ranking_strategy ?? '');
    setAcceptsPayments(!!stage.accepts_payments);
    setInfo({
      websiteUrl: stage.website_url || '',
      address: stage.address || '',
      city: stage.city || '',
      zipcode: stage.zipcode || '',
      latitude: stage.latitude != null ? String(stage.latitude) : '',
      longitude: stage.longitude != null ? String(stage.longitude) : '',
    });
  }, [stage]);

  const handleSave = async () => {
    if (!slug) return;
    setSaving(true);
    try {
      const updated = await competitionsService.updateCompetition(slug, {
        name: name.trim() || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        // Cleared on purpose is a real value — "no hour, open while the phase
        // lasts" — so an empty field is sent as null rather than dropped.
        entries_close_at: entriesCloseAt ? new Date(entriesCloseAt).toISOString() : null,
        round_refresh_seconds: refreshSeconds,
        // Empty is a real value — "the trophy's" — so it is sent, not dropped.
        ranking_strategy: rankingStrategy,
        website_url: info.websiteUrl || undefined,
        address: info.address || undefined,
        city: info.city || undefined,
        zipcode: info.zipcode || undefined,
        latitude: info.latitude ? parseFloat(info.latitude) : undefined,
        longitude: info.longitude ? parseFloat(info.longitude) : undefined,
      });
      setStage(updated);
      toast({ title: t('gymAdmin.saved') });
    } catch (error) {
      toast({
        title: t('gymAdmin.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Saved the moment it is flipped, like every other switch in the panel,
  // rather than with the form below: a switch that waits for "Save" reads as
  // done and is lost on reload. The stage itself is left alone, so whatever
  // is half-typed in the form is not reset from it.
  const handleAcceptsPayments = async (next: boolean) => {
    if (!slug) return;
    setAcceptsPayments(next);
    setSavingPayments(true);
    try {
      const updated = await competitionsService.updateCompetition(slug, { accepts_payments: next });
      setAcceptsPayments(!!updated.accepts_payments);
      toast({ title: t('gymAdmin.saved') });
    } catch (error) {
      setAcceptsPayments(!next);
      toast({
        title: t('gymAdmin.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSavingPayments(false);
    }
  };

  const handleStatusChange = async (status: LifecycleStatus) => {
    if (!slug) return;
    setMovingStatus(true);
    try {
      setStage(await competitionsService.updateStatus(slug, status as CompetitionStatus));
      toast({ title: t('gymAdmin.lifecycle.moved') });
    } catch (error) {
      // The server refuses a move the life cycle does not allow, and says why
      // — a competition people have entered cannot go back to draft.
      toast({
        title: t('gymAdmin.lifecycle.moveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setMovingStatus(false);
    }
  };

  if (loading) return <Skeleton className="h-96 w-full max-w-4xl" />;
  if (!stage) return null;

  return (
    <div className="max-w-4xl space-y-6">
      <StatusCard
        kind="competition"
        status={stage.status}
        available={stage.available_statuses ?? []}
        entriesOpen={stage.entries_open}
        saving={movingStatus}
        onChange={handleStatusChange}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('gymAdmin.info.imagesTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <LocationHeader
            locationId={stage.id}
            name={stage.name}
            bio={stage.bio}
            coverImageUrl={stage.cover_image_url}
            logoUrl={stage.logo_url}
            logoSmallUrl={stage.logo_small_url}
            galleryImages={stage.gallery_images ?? []}
            fallbackIcon={<Flag className="w-8 h-8 text-muted-foreground" />}
            isStaffMember
            isStaffAdmin
            hideName
            onBioUpdated={(bio) => setStage((s) => (s ? { ...s, bio } : s))}
            onCoverUpdated={(url) => setStage((s) => (s ? { ...s, cover_image_url: url } : s))}
            onLogoUpdated={(url, smallUrl) =>
              setStage((s) => (s ? { ...s, logo_url: url, logo_small_url: smallUrl } : s))
            }
            onGalleryUpdated={(imgs: GalleryImage[]) =>
              setStage((s) => (s ? { ...s, gallery_images: imgs } : s))
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('gymAdmin.info.detailsTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="stage-name">{t('gymAdmin.stages.name')}</Label>
            <Input id="stage-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          {/* Each date bounds the other, as in the create dialog. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <DateField
              id="stage-start-date"
              label={t('gymAdmin.stages.startDate')}
              value={startDate}
              max={endDate}
              onChange={setStartDate}
            />
            <DateField
              id="stage-end-date"
              label={t('gymAdmin.stages.endDate')}
              value={endDate}
              min={startDate}
              onChange={setEndDate}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="stage-entries-close-at">{t('gymAdmin.stages.entriesCloseAt')}</Label>
            <Input
              id="stage-entries-close-at"
              type="datetime-local"
              value={entriesCloseAt}
              onChange={(e) => setEntriesCloseAt(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t('gymAdmin.stages.entriesCloseAtHelp')}
            </p>
          </div>

          {/* How often the round pages refresh themselves on everybody's
              phone. Read off every round they fetch, so a change reaches the
              pages already open on their next refresh. */}
          <div className="space-y-2">
            <Label htmlFor="stage-round-refresh">{t('gymAdmin.stages.roundRefresh')}</Label>
            <Select value={String(refreshSeconds)} onValueChange={(next) => setRefreshSeconds(Number(next))}>
              <SelectTrigger id="stage-round-refresh" className="sm:w-64" data-testid="round-refresh-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROUND_REFRESH_CHOICES.map((seconds) => (
                  <SelectItem key={seconds} value={String(seconds)}>
                    {t(`gymAdmin.stages.roundRefreshChoices.${seconds}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{t('gymAdmin.stages.roundRefreshHelp')}</p>
          </div>

          {/* The places of every round here. On a stage, "the trophy's" unless
              this one runs under other rules. */}
          <RankingStrategySelect
            id="stage-ranking-strategy"
            value={rankingStrategy}
            options={stage.ranking_strategies ?? []}
            inherited={stage.trophy?.ranking_strategy}
            onChange={setRankingStrategy}
          />

          {/* Opt-in: not every competition takes a fee, and a "paid" switch
              on a free one is a column of red the desk has to ignore. */}
          <div className="flex items-start justify-between gap-4 rounded-md border p-3">
            <div className="space-y-1">
              <Label htmlFor="stage-accepts-payments">{t('gymAdmin.stages.acceptsPayments')}</Label>
              <p className="text-xs text-muted-foreground">{t('gymAdmin.stages.acceptsPaymentsHelp')}</p>
            </div>
            <Switch
              id="stage-accepts-payments"
              checked={acceptsPayments}
              disabled={savingPayments}
              onCheckedChange={handleAcceptsPayments}
              data-testid="accepts-payments-switch"
            />
          </div>

          {stage.venue && (
            <p className="text-sm text-muted-foreground">
              {t('gymAdmin.stages.hostedBy', { name: stage.venue.name })}
            </p>
          )}

          <GymInfoFields
            values={info}
            onChange={(patch) => setInfo((prev) => ({ ...prev, ...patch }))}
          />

          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('gymAdmin.saving') : t('gymAdmin.save')}
          </Button>
        </CardContent>
      </Card>

      {/* The regulation and the notices, the circuit's beside them on a
          stage: read only from here for now. */}
      {slug && <LocationDocumentsCard key={slug} slug={slug} stage={!!stage.trophy} />}
    </div>
  );
}
